import { useState, useEffect, useCallback, useMemo } from 'react';
import { fetchDepartmentsAsync, validateDepartmentQueryParams, processDepartmentsForDisplay } from '../services/departmentSvc';
import { Department, DepartmentQueryParams, DepartmentMeta } from '../types/department';
import { useApiError } from '@infrastructure/api/hooks/useApiError';
import { fetchAllPages } from '@core/utils/fetchAllPages';
import { normalizeAllForList } from '@core/utils/listQuery';

// NOTE: This hook intentionally does NOT use the shared `usePaginatedList`
// abstraction. Departments genuinely diverge from the church/district/nation
// list pattern: the API returns a flat paginated envelope (not `{ data, meta }`)
// and the `all=true` path uses `fetchAllPages` to bypass the per-page cap. Per
// the shared-by-default / override-when-truly-different principle, it stays
// custom rather than forcing those quirks into the generic hook.
interface UseDepartmentsReturn {
  departments: Department[];
  loading: boolean;
  error: string | null;
  pagination: DepartmentMeta | null;
  refetch: () => void;
  updateParams: (newParams: Partial<DepartmentQueryParams>) => void;
  validationErrors: string[];
}

const PARAM_KEYS: (keyof DepartmentQueryParams)[] = [
  'page',
  'perPage',
  'all',
  'departmentName',
  'departmentId',
  'isActive',
  'churchId',
  'districtId',
  'nationId',
];

function actionCodesEqual(a?: string[], b?: string[]): boolean {
  const left = a ?? [];
  const right = b ?? [];
  if (left.length !== right.length) return false;
  return left.every((code, index) => code === right[index]);
}

function departmentParamsEqual(a: DepartmentQueryParams, b: DepartmentQueryParams): boolean {
  return PARAM_KEYS.every((key) => a[key] === b[key]) && actionCodesEqual(a.actionCodes, b.actionCodes);
}

function hasDepartmentScope(params: DepartmentQueryParams): boolean {
  return Number(params.churchId ?? 0) > 0
    || Number(params.districtId ?? 0) > 0
    || Number(params.nationId ?? 0) > 0;
}

export const useDepartments = (initialParams: DepartmentQueryParams = {}): UseDepartmentsReturn => {
  const [departments, setDepartments] = useState<Department[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pagination, setPagination] = useState<DepartmentMeta | null>(null);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);
  const [params, setParams] = useState<DepartmentQueryParams>({
    perPage: 10,
    page: 1,
    ...initialParams
  });
  
  const showError = useApiError();

  const incomingParams = useMemo(
    () =>
      normalizeAllForList(
        {
          perPage: 10,
          page: 1,
          ...initialParams,
        },
        ['departmentName', 'departmentId']
      ),
    [
      initialParams.perPage,
      initialParams.page,
      initialParams.all,
      initialParams.departmentName,
      initialParams.departmentId,
      initialParams.isActive,
      initialParams.churchId,
      initialParams.districtId,
      initialParams.nationId,
      JSON.stringify(initialParams.actionCodes ?? []),
    ]
  );

  useEffect(() => {
    setParams((prev) => {
      const next: DepartmentQueryParams = {
        ...prev,
        all: incomingParams.all,
        departmentName: incomingParams.departmentName,
        departmentId: incomingParams.departmentId,
        isActive: incomingParams.isActive,
        churchId: incomingParams.churchId,
        districtId: incomingParams.districtId,
        nationId: incomingParams.nationId,
        actionCodes: incomingParams.actionCodes,
      };
      const filtersChanged = !departmentParamsEqual(
        { ...prev, page: 0, perPage: 0 },
        { ...next, page: 0, perPage: 0 }
      );
      if (filtersChanged) {
        next.page = 1;
      }
      return departmentParamsEqual(prev, next) ? prev : next;
    });
  }, [incomingParams]);

  const fetchData = useCallback(async () => {
    if (!hasDepartmentScope(params)) {
      setDepartments([]);
      setPagination(null);
      setError(null);
      setValidationErrors([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);
    setValidationErrors([]);
    
    try {
      // Validate parameters using service layer
      const validation = validateDepartmentQueryParams(params);
      if (!validation.isValid) {
        setValidationErrors(validation.errors);
        setDepartments([]);
        setPagination(null);
        return;
      }

      const effectiveParams = normalizeAllForList(
        validation.processedParams,
        ['departmentName', 'departmentId']
      );

      if (effectiveParams.all) {
        // When fetching all, use multi-page fetch to avoid 20-item cap
        const allItems = await fetchAllPages<Department>((page) =>
          fetchDepartmentsAsync({ ...effectiveParams, page })
        );
        const processedDepartments = processDepartmentsForDisplay(allItems);
        setDepartments(processedDepartments);
        setPagination(null);
      } else {
        // Normal paginated fetch
        const response = await fetchDepartmentsAsync(effectiveParams);

        if (response.isSuccess()) {
          const departmentData = response.getData()!;
          const processedDepartments = processDepartmentsForDisplay(departmentData.data);
          setDepartments(processedDepartments);
          setPagination({
            current_page: departmentData.current_page,
            first_page_url: departmentData.first_page_url,
            from: departmentData.from,
            last_page: departmentData.last_page,
            last_page_url: departmentData.last_page_url,
            links: departmentData.links,
            next_page_url: departmentData.next_page_url,
            path: departmentData.path,
            per_page: departmentData.per_page,
            prev_page_url: departmentData.prev_page_url,
            to: departmentData.to,
            total: departmentData.total,
          });
        } else {
          const errorData = response.getError();
          setError(errorData?.detail || 'Failed to fetch departments');
          if (errorData) {
            showError(errorData);
          }
          setDepartments([]);
          setPagination(null);
        }
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch departments';
      setError(errorMessage);
      setDepartments([]);
      setPagination(null);
    } finally {
      setLoading(false);
    }
  }, [params, showError]);

  useEffect(() => {
    void fetchData();
  }, [fetchData]);

  const refetch = useCallback(() => {
    void fetchData();
  }, [fetchData]);

  const updateParams = useCallback((newParams: Partial<DepartmentQueryParams>) => {
    setParams((prev) => {
      const merged = {
        ...prev,
        ...newParams,
        // Business rule: Reset to page 1 when changing filters
        page: newParams.departmentName || newParams.departmentId ? 1 : (newParams.page || prev.page),
      };

      const next = normalizeAllForList(merged, ['departmentName', 'departmentId']);
      return departmentParamsEqual(prev, next) ? prev : next;
    });
  }, []);

  return {
    departments,
    loading,
    error,
    pagination,
    refetch,
    updateParams,
    validationErrors
  };
};
